import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ChevronRightIcon, ActivityIcon, StethoscopeIcon, FileTextIcon, CheckCircle2Icon } from 'lucide-react';
import { IntakeExpandableCard } from './IntakeExpandableCard';
import { LumpTypesVisual } from './LumpTypesVisual';
import { getText, isRtl, type Lang } from './translations';
type CardType = 'what' | 'how' | 'bring' | null;
export function LumpRewardSlide({
  onNext,
  onBack,
  lang = 'en' as Lang
}: {
  onNext: () => void;
  onBack: () => void;
  lang?: Lang;
}) {
  const [activeCard, setActiveCard] = useState<CardType>(null);
  const rtl = isRtl(lang);
  return <div className="flex flex-col h-full px-6 py-8 relative z-10 bg-white" dir={rtl ? 'rtl' : 'ltr'} data-id="element-483">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6" data-id="element-484">
        <button onClick={onBack} className="p-2 -ml-2 text-slate-400 hover:text-stone-600 transition-colors bg-white rounded-full shadow-sm border border-stone-100" aria-label="Go back" data-id="element-485">
          <ArrowLeftIcon className={`w-5 h-5 ${rtl ? 'rotate-180' : ''}`} data-id="element-486" />
        </button>
      </div>

      <div className="flex-1 flex flex-col max-w-sm mx-auto w-full overflow-y-auto no-scrollbar pb-6" data-id="element-487">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-6" data-id="element-488">
          <div className="inline-flex items-center space-x-2 rtl:space-x-reverse bg-nhs-blue/10 text-nhs-blue px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4 border border-nhs-blue/20" data-id="element-489">
            <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-490" />
            <span data-id="element-491">{getText('insight', lang)}</span>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 leading-tight mb-4" data-id="element-492">
            {getText('lumpInsightTitle', lang)}
          </h2>

          <div className="bg-white p-5 rounded-2xl border border-stone-200 shadow-sm mb-8" data-id="element-493">
            <p className="text-stone-600 leading-relaxed" data-id="element-494">
              {getText('lumpInsightBody', lang)}
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-495">
          <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-4 px-1" data-id="element-496">
            Would you like to learn more?
          </h3>

          <div className="space-y-3 mb-8" data-id="element-497">
            <button onClick={() => setActiveCard('what')} className="w-full flex items-center justify-between p-4 bg-white border border-stone-200 rounded-xl shadow-sm hover:border-nhs-blue/50 hover:bg-nhs-blue/5 transition-all group text-left" data-id="element-498">
              <div className="flex items-center space-x-4 rtl:space-x-reverse" data-id="element-499">
                <div className="bg-nhs-blue/10 p-2 rounded-lg text-nhs-blue group-hover:bg-nhs-blue/20 transition-colors" data-id="element-500">
                  <ActivityIcon className="w-5 h-5" data-id="element-501" />
                </div>
                <span className="font-medium text-stone-700 group-hover:text-nhs-blue transition-colors" data-id="element-502">
                  {getText('whatLumpCanBe', lang)}
                </span>
              </div>
              <ChevronRightIcon className={`w-5 h-5 text-slate-400 group-hover:text-nhs-blue group-hover:translate-x-1 transition-all ${rtl ? 'rotate-180' : ''}`} data-id="element-503" />
            </button>

            <button onClick={() => setActiveCard('how')} className="w-full flex items-center justify-between p-4 bg-white border border-stone-200 rounded-xl shadow-sm hover:border-nhs-blue/50 hover:bg-nhs-blue/5 transition-all group text-left" data-id="element-504">
              <div className="flex items-center space-x-4 rtl:space-x-reverse" data-id="element-505">
                <div className="bg-nhs-blue/10 p-2 rounded-lg text-nhs-blue group-hover:bg-nhs-blue/20 transition-colors" data-id="element-506">
                  <StethoscopeIcon className="w-5 h-5" data-id="element-507" />
                </div>
                <span className="font-medium text-stone-700 group-hover:text-nhs-blue transition-colors" data-id="element-508">
                  {getText('howClinicExamines', lang)}
                </span>
              </div>
              <ChevronRightIcon className={`w-5 h-5 text-slate-400 group-hover:text-nhs-blue group-hover:translate-x-1 transition-all ${rtl ? 'rotate-180' : ''}`} data-id="element-509" />
            </button>

            <button onClick={() => setActiveCard('bring')} className="w-full flex items-center justify-between p-4 bg-white border border-stone-200 rounded-xl shadow-sm hover:border-nhs-blue/50 hover:bg-nhs-blue/5 transition-all group text-left" data-id="element-510">
              <div className="flex items-center space-x-4 rtl:space-x-reverse" data-id="element-511">
                <div className="bg-nhs-blue/10 p-2 rounded-lg text-nhs-blue group-hover:bg-nhs-blue/20 transition-colors" data-id="element-512">
                  <FileTextIcon className="w-5 h-5" data-id="element-513" />
                </div>
                <span className="font-medium text-stone-700 group-hover:text-nhs-blue transition-colors" data-id="element-514">
                  {getText('whatToBring', lang)}
                </span>
              </div>
              <ChevronRightIcon className={`w-5 h-5 text-slate-400 group-hover:text-nhs-blue group-hover:translate-x-1 transition-all ${rtl ? 'rotate-180' : ''}`} data-id="element-515" />
            </button>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className="mt-auto pt-4" data-id="element-516">
          <button onClick={onNext} className="w-full py-4 bg-nhs-blue text-white rounded-xl font-semibold text-lg flex items-center justify-center hover:bg-nhs-darkblue transition-colors shadow-md shadow-nhs-blue/20 active:scale-[0.98]" data-id="element-517">
            {getText('continue', lang)}
          </button>
        </motion.div>
      </div>

      {/* Content Cards */}
      <IntakeExpandableCard isOpen={activeCard === 'what'} onClose={() => setActiveCard(null)} title="Common causes of breast lumps" data-id="element-518">
        <div className="space-y-6" data-id="element-519">
          <LumpTypesVisual data-id="element-520" />
          <button onClick={() => setActiveCard(null)} className="w-full py-3 bg-stone-100 text-stone-700 rounded-xl font-medium hover:bg-stone-200 transition-colors" data-id="element-521">
            Back
          </button>
        </div>
      </IntakeExpandableCard>

      <IntakeExpandableCard isOpen={activeCard === 'how'} onClose={() => setActiveCard(null)} title="What happens at a breast clinic visit" data-id="element-522">
        <div className="space-y-6" data-id="element-523">
          <p className="text-stone-600 leading-relaxed" data-id="element-524">
            Your specialist team may perform several checks to understand your
            symptoms.
          </p>

          <div className="relative border-l-2 border-stone-200 ml-3 space-y-6 my-8" data-id="element-525">
            {['Referral & History', 'Clinical examination', 'Ultrasound or mammogram', 'Possible biopsy', 'Results discussion'].map((step, i) => <div key={i} className="relative pl-6" data-id="element-526">
                <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-white border-2 border-nhs-blue" data-id="element-527" />
                <p className="font-medium text-slate-800" data-id="element-528">{step}</p>
              </div>)}
          </div>

          <button onClick={() => setActiveCard(null)} className="w-full py-3 bg-stone-100 text-stone-700 rounded-xl font-medium hover:bg-stone-200 transition-colors" data-id="element-529">
            Back
          </button>
        </div>
      </IntakeExpandableCard>

      <IntakeExpandableCard isOpen={activeCard === 'bring'} onClose={() => setActiveCard(null)} title="Preparing for your visit" data-id="element-530">
        <div className="space-y-6" data-id="element-531">
          <p className="text-stone-600 leading-relaxed" data-id="element-532">
            To help your clinic prepare, please bring:
          </p>

          <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-4 shadow-sm" data-id="element-533">
            <div className="flex items-start space-x-3" data-id="element-534">
              <CheckCircle2Icon className="w-5 h-5 text-nhs-blue flex-shrink-0 mt-0.5" data-id="element-535" />
              <span className="text-stone-700" data-id="element-536">
                Previous imaging results if available
              </span>
            </div>
            <div className="w-full h-px bg-stone-100" data-id="element-537" />
            <div className="flex items-start space-x-3" data-id="element-538">
              <CheckCircle2Icon className="w-5 h-5 text-nhs-blue flex-shrink-0 mt-0.5" data-id="element-539" />
              <span className="text-stone-700" data-id="element-540">
                Any reports from other hospitals
              </span>
            </div>
            <div className="w-full h-px bg-stone-100" data-id="element-541" />
            <div className="flex items-start space-x-3" data-id="element-542">
              <CheckCircle2Icon className="w-5 h-5 text-nhs-blue flex-shrink-0 mt-0.5" data-id="element-543" />
              <span className="text-stone-700" data-id="element-544">
                A list of medications you take
              </span>
            </div>
          </div>

          <button onClick={() => setActiveCard(null)} className="w-full py-3 bg-stone-100 text-stone-700 rounded-xl font-medium hover:bg-stone-200 transition-colors mt-4" data-id="element-545">
            Back
          </button>
        </div>
      </IntakeExpandableCard>
    </div>;
}